import { lazy, Suspense, useEffect } from 'react';
import {
  createBrowserRouter,
  Navigate,
  redirect,
  RouterProvider,
  useParams
} from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Slide, ToastContainer } from 'react-toastify';
import { getCookie } from './utils/common';
import {
  classLoader,
  feedbackLoader,
  loginLoader,
  manageClassLoader,
  WhiteBoardLoader
} from './loaders';
import Loader from './components/Loader';
import NotFound from './components/NotFound';
import ErrorBoundary from './components/ErrorBoundary';
import WhiteBoard from './pages/WhiteBoard';
import DemoEndPage from './pages/DemoEndPage';

const Home = lazy(() => import('./pages/Home'));
const Login = lazy(() => import('./pages/Login'));
const Leads = lazy(() => import('./pages/Leads'));
const Referrals = lazy(() => import('./pages/Referrals'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const ManageClass = lazy(() => import('./pages/ManageClass'));
const ManageGuests = lazy(() => import('./pages/ManageGuests'));
const Feedback = lazy(() => import('./pages/Feedback'));
const RTCClass = lazy(() => import('./components/RTCClass'));
const MainLayout = lazy(() => import('./components/MainLayout'));
const LoginLayout = lazy(() => import('./components/LoginLayout'));
const DashboardLayout = lazy(() => import('./components/DashboardLayout'));
const GuestLogin = lazy(() => import('./pages/GuestLogin'));
const StudentLogin = lazy(() => import('./pages/StudentLogin'));
const Over = lazy(() => import('./pages/Over'));

const user = getCookie('crystal-user');

const router = createBrowserRouter([
  {
    path: '/',
    ErrorBoundary,
    hydrateFallbackElement: <Loader />,
    children: [
      {
        path: '/class/:classParams',
        Component: RTCClass,
        loader: classLoader,
        hydrateFallbackElement: <Loader />
      },
      {
        path: '/attend/*',
        loader: ({ request }) => {
          const [, params] = request.url.split('/attend/');
          return redirect(`/class/${params}`);
        }
      },
      {
        path: '/whiteboard/:classParams',
        Component: WhiteBoard,
        loader: WhiteBoardLoader
      },
      {
        path: '/',
        Component: MainLayout,
        children: [
          {
            index: true,
            Component: user ? Home : () => <Navigate to={'/login'} replace />
          },
          {
            path: '/over',
            Component: Over
          },
          {
            path: '/login',
            Component: !user ? Login : () => <Navigate to={'/'} replace />
          },
          {
            path: '/feedback/:id',
            Component: Feedback,
            loader: feedbackLoader
          },
          {
            path: '/dashboard',
            Component: DashboardLayout,
            children: [
              {
                index: true,
                Component: () => (
                  <Navigate
                    to={user ? '/dashboard/classes' : '/login?redirect=/dashboard/classes'}
                    replace
                  />
                )
              },
              {
                path: 'classes',
                Component: user
                  ? Dashboard
                  : () => <Navigate to="/login?redirect=/dashboard/classes" replace />
              },
              {
                path: ':roomId',
                Component: user
                  ? ManageClass
                  : () => {
                      const params = useParams();
                      return (
                        <Navigate to={`/login?redirect=/dashbboard/${params.roomId}`} replace />
                      );
                    },
                loader: manageClassLoader
              },
              {
                path: 'guests',
                Component: user
                  ? ManageGuests
                  : () => <Navigate to="/login?redirect=/dashboard/guests" replace />
              },
              {
                path: 'leads',
                Component: user
                  ? Leads
                  : () => <Navigate to="/login?redirect=/dashboard/leads" replace />
              },
              {
                path: 'referrals',
                Component: user
                  ? Referrals
                  : () => <Navigate to="/login?redirect=/dashboard/referrals" replace />
              }
            ]
          },
          {
            path: '*',
            Component: NotFound
          }
        ]
      },
      {
        path: '/login',
        Component: LoginLayout,
        children: [
          {
            path: 'guest/:classParams',
            Component: GuestLogin,
            loader: loginLoader
          },
          {
            path: 'psid/:classParams',
            Component: StudentLogin,
            loader: loginLoader
          }
        ]
      },
      {
        path: '/demo-end',
        Component: DemoEndPage
      }
    ]
  }
]);

function App() {
  useEffect(() => {
    const addScripts = () => {
      const gtmScript = document.createElement('script');
      gtmScript.type = 'text/javascript';
      gtmScript.innerHTML = `(function (w, d, s, l, i) {
          w[l] = w[l] || [];
          w[l].push({'gtm.start': new Date().getTime(), event: 'gtm.js'});
          var f = d.getElementsByTagName(s)[0],
          j = d.createElement(s),
          dl = l != 'dataLayer' ? '&l=' + l : '';
          j.defer = 1;
          j.async = 0;
          j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
          f.parentNode.insertBefore(j, f);
        })(window, document, 'script', 'dataLayer', '${import.meta.env.VITE_PUBLIC_GTM_KEY}');`;

      // const clarityScript = document.createElement('script');
      // clarityScript.type = 'text/javascript';
      // clarityScript.innerHTML = `(function (c, l, a, r, i, t, y) {
      //     c[a] =
      //       c[a] ||
      //       function () {
      //         (c[a].q = c[a].q || []).push(arguments);
      //       };
      //     t = l.createElement(r);
      //     t.async = 1;
      //     t.src = 'https://www.clarity.ms/tag/' + i;
      //     y = l.getElementsByTagName(r)[0];
      //     y.parentNode.insertBefore(t, y);
      //   })(window, document, 'clarity', 'script', '${import.meta.env.VITE_PUBLIC_CLARITY_KEY}');`;

      setTimeout(() => {
        document.head.appendChild(gtmScript);
        window.dispatchEvent(new Event('gtm_added'));
        // document.head.appendChild(clarityScript);
      }, 5000);
    };

    if (document.readyState !== 'loading') {
      addScripts();
    } else {
      document.addEventListener('readystatechange', addScripts);
      return () => document.removeEventListener('readystatechange', addScripts);
    }
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', true);
  }, []);

  return (
    <Suspense fallback={<>loading</>}>
      <Helmet>
        <link rel="preload" href="/images/404-page-dweb.webp" as="image" />
        <link rel="preload" href="/images/ad_logo.webp" as="image" />
      </Helmet>
      <RouterProvider router={router} />
      <ToastContainer
        position="top-left"
        stacked
        hideProgressBar
        theme="dark"
        transition={Slide}
        limit={6}
      />
    </Suspense>
  );
}

export default App;
